/**
 * Service to interact with OpenAI's API
 */
class OpenAIService {
  constructor() {
    this.apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    this.apiEndpoint = 'https://api.openai.com/v1/responses';
    this.model = 'gpt-4.1-2025-04-14';
  }

  /**
   * Generate a solution for a NEET question using OpenAI API
   * @param {Object} question - Question object
   * @returns {Promise<Object>} - Generated solution object with solution text and correct answer
   */
  async generateSolution(question) {
    if (!this.apiKey) {
      throw new Error('OpenAI API key not set. Please set REACT_APP_OPENAI_API_KEY in .env file.');
    }

    try {
      const prompt = `
You are an expert NEET exam tutor. Generate a detailed step-by-step solution for the following NEET ${question.subject} question.

Question: ${question.text}

Options:
A: ${question.options.A}
B: ${question.options.B}
C: ${question.options.C}
D: ${question.options.D}

Provide your response in the following format:

CORRECT_ANSWER: [LETTER]
(Just a single letter A, B, C, or D as the correct answer)

SOLUTION:
[Your step-by-step explanation here]
`;

      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: this.model,
          input: prompt
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`OpenAI API Error: ${errorData.error?.message || 'Unknown error'}`);
      }

      const data = await response.json();
      
      // Extract the text from the new API response structure
      let responseText = '';
      if (data.output && Array.isArray(data.output) && data.output.length > 0) {
        const message = data.output[0];
        if (message.content && Array.isArray(message.content)) {
          // Find the first content item with type 'output_text'
          const textContent = message.content.find(c => c.type === 'output_text');
          if (textContent && textContent.text) {
            responseText = textContent.text;
          }
        }
      }
      
      if (!responseText) {
        console.log('OpenAI response structure:', JSON.stringify(data, null, 2));
        throw new Error('Unable to parse OpenAI response. See console for details.');
      }
      
      // Extract correct answer from the response
      const answerMatch = responseText.match(/CORRECT_ANSWER:\s*([ABCD])/i);
      const correctAnswer = answerMatch ? answerMatch[1].toUpperCase() : null;
      
      // Extract solution text (everything after "SOLUTION:")
      const solutionMatch = responseText.match(/SOLUTION:([\s\S]*)/i);
      const solutionText = solutionMatch ? solutionMatch[1].trim() : responseText;
      
      if (!correctAnswer) {
        throw new Error('Could not extract correct answer from OpenAI response');
      }
      
      return {
        solution: solutionText,
        correctAnswer: correctAnswer
      };
    } catch (error) {
      console.error("Error calling OpenAI API:", error);
      throw error;
    }
  }

  /**
   * Generate a solution for a NEET question using GPT-4 Vision API and the full image
   * @param {Object} question - Question object
   * @param {File} imageFile - The image file containing the question paper
   * @returns {Promise<Object>} - Generated solution object with solution text and correct answer
   */
  async generateSolutionFromImage(question, imageFile) {
    if (!this.apiKey) {
      throw new Error('OpenAI API key not set. Please set REACT_APP_OPENAI_API_KEY in .env file.');
    }

    try {
      const base64Image = await this.fileToBase64(imageFile);
      
      const prompt = `You are looking at a NEET exam paper image. Focus on question number ${question.questionNumber} and provide its solution.

Please provide your response in this format:
CORRECT_ANSWER: [LETTER]
(Just a single letter A, B, C, or D as the correct answer)

SOLUTION:
[Your step-by-step explanation here]`;

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4-vision-preview',
          messages: [
            {
              role: 'user',
              content: [
                {
                  type: 'text',
                  text: prompt
                },
                {
                  type: 'image_url',
                  image_url: {
                    url: `data:${imageFile.type};base64,${base64Image}`
                  }
                }
              ]
            }
          ],
          max_tokens: 500
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`OpenAI API Error: ${errorData.error?.message || 'Unknown error'}`);
      }

      const data = await response.json();
      const responseText = data.choices[0].message.content;
      
      // Extract correct answer from the response
      const answerMatch = responseText.match(/CORRECT_ANSWER:\s*([ABCD])/i);
      const correctAnswer = answerMatch ? answerMatch[1].toUpperCase() : null;
      
      // Extract solution text (everything after "SOLUTION:")
      const solutionMatch = responseText.match(/SOLUTION:([\s\S]*)/i);
      const solutionText = solutionMatch ? solutionMatch[1].trim() : responseText;
      
      if (!correctAnswer) {
        throw new Error('Could not extract correct answer from OpenAI response');
      }
      
      return {
        solution: solutionText,
        correctAnswer: correctAnswer
      };
    } catch (error) {
      console.error("Error calling OpenAI Vision API:", error);
      throw error;
    }
  }

  /**
   * Convert image to base64 string
   * @param {File} file - The image file to convert
   * @returns {Promise<string>} - Base64 encoded image
   */
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Extract the base64 part of the data URL
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = error => reject(error);
    });
  }
  
  /**
   * Generate solutions for multiple questions in batch
   * @param {Array} questions - Array of question objects
   * @returns {Promise<Array>} - Array of solution objects
   */
  async generateSolutions(questions) {
    const solutions = [];
    
    // Process questions sequentially to avoid rate limiting
    for (const question of questions) {
      try {
        const solutionData = await this.generateSolution(question);
        solutions.push({
          questionId: question.id,
          solution: solutionData.solution,
          correctAnswer: solutionData.correctAnswer
        });
      } catch (error) {
        console.error(`Error generating OpenAI solution for question ${question.id}:`, error);
        // Add a placeholder for failed solutions
        solutions.push({
          questionId: question.id,
          solution: "Error generating solution with OpenAI. Please try again.",
          correctAnswer: null
        });
      }
      
      // Add a small delay between requests to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    return solutions;
  }
}

// Export a singleton instance
export const openaiService = new OpenAIService();